import React, { useId, useState, useEffect, useRef, useCallback } from 'react';
import DatePicker, { DateObject, DatePickerRef } from 'react-multi-date-picker';
import Dropdown from './dropdown';

type destinationProp = {
  label: string;
  options: {
    label: string;
    value: string;
    default: boolean;
  }[];
};

type datesProp = {
  label: string;
  placeholder: string;
  urlValArrive: string;
  urlValDepart: string;
  clearSelectionText: string;
  selectDatesText: string;
};

type promoProp = {
  label: string;
  urlVal: string;
  placeholder: string;
};

type guestProp = {
  label: string;
  urlVal: string;
  maxGuest: number;
  optionLabel: string;
};

type BookingHeroProps = {
  destination: string;
  dates: string;
  guests: string;
  promo: string;
  cta: string;
};

const BookingHero: React.FC<BookingHeroProps> = ({ destination, dates, guests, promo, cta }) => {
  const destinationObj: destinationProp = JSON.parse(destination);
  const destId = useId();
  const promoObj: promoProp = JSON.parse(promo);
  const promoId = useId();
  const guestsObj: guestProp = JSON.parse(guests);
  const guestId = useId();
  const datesObj: datesProp = JSON.parse(dates);
  const [selectedValues, setSelectedValues] = useState<{ [key: string]: string | DateObject }>({});
  const [redirectLink, setRedirectLink] = useState<string>('');
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const datePickerRef = useRef<DatePickerRef>(null);
  const [calendarPosition, setCalendarPosition] = useState<'top' | 'bottom'>('top');

  const offset = 450;

  useEffect(() => {
    const storedValues = sessionStorage.getItem('selectedValues');
    const parsedValues = storedValues ? JSON.parse(storedValues) : null;

    if (parsedValues && parsedValues.destination) {
      const destination = destinationObj.options.find(option => option.default)?.value;
      const values = destination ? { ...parsedValues, destination } : parsedValues;

      setSelectedValues(values);
    } else {
      setSelectedValues({
        destination: destinationObj.options.find(option => option.default)
          ? destinationObj.options.find(option => option.default)?.value ||
            destinationObj.options[0].value
          : destinationObj.options[0].value,
        [datesObj.urlValArrive]: '',
        [datesObj.urlValDepart]: '',
        [guestsObj.urlVal]: '1',
        [promoObj.urlVal]: '',
        dateArrive: '',
        dateDepart: ''
      });
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('selectedValues', JSON.stringify(selectedValues));
    handleRedirect();
  }, [selectedValues]);

  const guestsOptions = Array.from({ length: guestsObj.maxGuest }, (_, index) =>
    guestsObj.optionLabel.replace('{{guest}}', (index + 1).toString())
  );

  const handleRedirect = () => {
    let relPath = '';
    Object.entries(selectedValues).map(([key, value]) => {
      if (key === 'destination') {
        relPath = `${value}?`;
        return;
      } else if (key === 'dateArrive' || key === 'dateDepart') {
        return;
      }

      if (value !== '') {
        relPath = `${relPath}${encodeURIComponent(key)}=${encodeURIComponent(value as string)}&`;
      }
    });

    relPath = relPath.slice(0, -1);

    setRedirectLink(relPath);
  };

  const handleCalendarValue = (calendarDates: DateObject[]) => {
    if (calendarDates) {
      calendarDates.forEach((date: DateObject, index) => {
        if (index === 0) {
          setSelectedValues({
            ...selectedValues,
            [datesObj.urlValArrive]: `${String(date.month.number).padStart(2, '0')}/${String(date.day).padStart(2, '0')}/${date.year}`,
            dateArrive: date,
            [datesObj.urlValDepart]: '',
            dateDepart: ''
          });
        } else {
          setSelectedValues({
            ...selectedValues,
            [datesObj.urlValDepart]: `${String(date.month.number).padStart(2, '0')}/${String(date.day).padStart(2, '0')}/${date.year}`,
            dateDepart: date
          });
        }
      });
    }
  };

  const handleCloseCalendar = () => {
    if (datePickerRef.current) {
      datePickerRef.current.closeCalendar();
    }
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const isTab = e.key === 'Tab';
    const isShift = e.shiftKey;
    if (isShift || !isTab) return;

    const target = e.target as HTMLElement;

    const classLeft = 'rmdp-left';
    const classDay = 'rmdp-day';
    const classClear = 'booking-hero__clear-selection';
    const classSelect = 'booking-hero__select-date';
    const classClose = 'booking-hero__calendar-close-button';

    if (target.classList.contains(classDay)) {
      setTimeout(() => {
        (document.querySelector(`.rmdp-wrapper .${classClear}`) as HTMLElement)?.focus();
      });
      return;
    }

    if (target.classList.contains(classSelect)) {
      setTimeout(() => {
        (document.querySelector(`.rmdp-wrapper .${classClose}`) as HTMLElement)?.focus();
      });
      return;
    }

    if (target.classList.contains(classClose)) {
      setTimeout(() => {
        (document.querySelector(`.rmdp-wrapper .${classLeft}`) as HTMLElement)?.focus();
      });
      return;
    }
  }, []);

  useEffect(() => {
    if (!isCalendarOpen) return;

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCalendarOpen, handleKeyDown]);

  const throttle = (func: () => void, delay: number) => {
    let lastCall = 0;
    return () => {
      const now = Date.now();
      if (now - lastCall < delay) return;
      lastCall = now;
      func();
    };
  };

  const debouncedHandleScroll = useCallback(
    throttle(() => {
      if (!datePickerRef.current) return;

      const datePickerBottom = datePickerRef.current?.getBoundingClientRect().bottom;
      const pageBottom = window.innerHeight;
      const bottomSpace = pageBottom - (datePickerBottom ?? 0);
      const topSpace = datePickerRef.current?.getBoundingClientRect().top ?? 0;

      if (bottomSpace > offset || topSpace < offset) {
        setCalendarPosition('bottom');
      } else {
        setCalendarPosition('top');
      }
    }, 100),
    []
  );

  useEffect(() => {
    if (!datePickerRef.current) return;

    debouncedHandleScroll();
    window.addEventListener('scroll', debouncedHandleScroll);

    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, [debouncedHandleScroll]);

  return (
    <div className="booking-hero">
      <Dropdown
        id={destId}
        label={destinationObj.label}
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        options={destinationObj}
        dropdownType="destination"
        position={calendarPosition}
      />
      <div className="booking-hero__field">
        <label htmlFor="booking-dates" className="booking-hero__label">
          {datesObj.label}
        </label>
        <DatePicker
          ref={datePickerRef}
          dateSeparator=" - "
          inputClass="booking-hero__calendar"
          arrowClassName="booking-hero__calendar-arrow"
          containerClassName={`booking-hero__calendar-container ${isCalendarOpen ? 'booking-hero__calendar-container--open' : ''}`}
          onChange={handleCalendarValue}
          range={true}
          numberOfMonths={2}
          weekStartDayIndex={0}
          highlightToday={false}
          monthYearSeparator=" "
          format="MMM DD, YYYY"
          placeholder={datesObj.placeholder}
          minDate={new Date().setHours(0, 0, 0, 0)}
          value={[selectedValues.dateArrive as DateObject, selectedValues.dateDepart as DateObject]}
          id="booking-dates"
          onOpen={() => {
            setIsCalendarOpen(true);
          }}
          onClose={() => {
            setSelectedValues({ ...selectedValues });
            setIsCalendarOpen(false);
          }}
          editable={false}
          calendarPosition={`${calendarPosition}-start`}
          fixMainPosition={true}
          offsetY={calendarPosition === 'top' ? 24 : -3}
          className={isCalendarOpen ? 'open' : undefined}
          render={(value, openCalendar) => {
            return (
              <button onClick={openCalendar} className="booking-hero__calendar">
                {value ? value : datesObj.placeholder}
              </button>
            );
          }}
        >
          <div className="booking-hero__calendar-button-wrapper">
            <button
              type="button"
              className="link booking-hero__clear-selection"
              onClick={() =>
                setSelectedValues({
                  ...selectedValues,
                  [datesObj.urlValArrive]: '',
                  dateArrive: '',
                  [datesObj.urlValDepart]: '',
                  dateDepart: ''
                })
              }
            >
              {datesObj.clearSelectionText}
            </button>

            <button
              type="button"
              className="btn btn--secondary booking-hero__select-date"
              onClick={handleCloseCalendar}
            >
              {datesObj.selectDatesText}
            </button>
          </div>
          <div className="booking-hero__calendar-close-button-container">
            <button
              type="button"
              className="booking-hero__calendar-close-button"
              aria-label="close-calendar"
              onClick={handleCloseCalendar}
            ></button>
          </div>
        </DatePicker>
      </div>

      <Dropdown
        id={guestId}
        label={guestsObj.label}
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        options={guestsOptions}
        dropdownType={guestsObj.urlVal}
        position={calendarPosition}
      />
      <div className="booking-hero__field">
        <label className="booking-hero__label" htmlFor={promoId}>
          {promoObj.label}
        </label>
        <input
          className="booking-hero__input booking-hero__input--promo"
          id={promoId}
          type="text"
          placeholder={promoObj.placeholder}
          value={(selectedValues[promoObj.urlVal] as string) ?? ''}
          onChange={e =>
            setSelectedValues({ ...selectedValues, [promoObj.urlVal]: e.target.value })
          }
        />
      </div>
      <a href={redirectLink} className="booking-hero__cta btn btn--secondary">
        {cta}
      </a>
    </div>
  );
};
export default BookingHero;
